import apiClient from './client';
import { Customer, CreateCustomerRequest, CheckCustomerSuggestion, CustomerDepositSuggestion, Check, CustomerDeposit } from '../types';

export interface PotentialCustomer {
  suggestion_id: string;
  client_name: string;
  status: 'pending' | 'accepted' | 'rejected';
  referenced_checks: string[];
  referenced_deposits: string[];
  entity_type?: 'p' | 'c' | null;  // 'p' = person, 'c' = company
}

export const customersApi = {
  /**
   * List all customers for a client
   */
  listCustomers: async (clientId: string): Promise<Customer[]> => {
    const response = await apiClient.get<Customer[]>(
      `/clients/${clientId}/customers`,
      { params: { limit: 10000 } }
    );
    return response.data;
  },

  /**
   * Get a specific customer
   */
  getCustomer: async (customerId: string): Promise<Customer> => {
    const response = await apiClient.get<Customer>(`/customers/${customerId}`);
    return response.data;
  },

  /**
   * Create a new customer
   */
  createCustomer: async (
    clientId: string,
    data: CreateCustomerRequest
  ): Promise<Customer> => {
    const response = await apiClient.post<Customer>(
      `/clients/${clientId}/customers`,
      data
    );
    return response.data;
  },

  /**
   * Update a customer
   */
  updateCustomer: async (
    clientId: string,
    customerId: string,
    data: Partial<Customer>
  ): Promise<Customer> => {
    const response = await apiClient.put<Customer>(
      `/clients/${clientId}/customers/${customerId}`,
      data
    );
    return response.data;
  },

  /**
   * Merge two customers
   */
  mergeCustomers: async (
    clientId: string,
    sourceCustomerId: string,
    targetCustomerId: string
  ): Promise<Customer> => {
    const response = await apiClient.post<Customer>(
      `/clients/${clientId}/customers/merge`,
      {
        source_customer_ids: [sourceCustomerId],
        target_customer_id: targetCustomerId,
      }
    );
    return response.data;
  },

  /**
   * Delete a customer
   */
  deleteCustomer: async (customerId: string): Promise<void> => {
    await apiClient.delete(`/customers/${customerId}`);
  },

  /**
   * Get check customer suggestions
   */
  getCheckSuggestions: async (
    clientId: string
  ): Promise<CheckCustomerSuggestion[]> => {
    const response = await apiClient.get<CheckCustomerSuggestion[]>(
      `/clients/${clientId}/customer-suggestions/checks`
    );
    return response.data;
  },

  /**
   * Get deposit customer suggestions
   */
  getDepositSuggestions: async (
    clientId: string
  ): Promise<CustomerDepositSuggestion[]> => {
    const response = await apiClient.get<CustomerDepositSuggestion[]>(
      `/clients/${clientId}/customer-suggestions/deposits`
    );
    return response.data;
  },

  /**
   * Accept a customer suggestion
   */
  acceptSuggestion: async (suggestionId: string, type: 'check' | 'deposit'): Promise<void> => {
    await apiClient.post(`/customer-suggestions/${type}/${suggestionId}/accept`);
  },

  /**
   * Reject a customer suggestion
   */
  rejectSuggestion: async (suggestionId: string, type: 'check' | 'deposit'): Promise<void> => {
    await apiClient.post(`/customer-suggestions/${type}/${suggestionId}/reject`);
  },

  /**
   * Get potential customers (AI-generated customer suggestions with grouped checks/deposits)
   */
  getPotentialCustomers: async (clientId: string): Promise<PotentialCustomer[]> => {
    const response = await apiClient.get<PotentialCustomer[]>(
      `/clients/${clientId}/potential-customers`
    );
    return response.data;
  },

  /**
   * Get unassigned checks for a client
   */
  getUnassignedChecks: async (clientId: string): Promise<Check[]> => {
    const response = await apiClient.get<Check[]>(
      `/clients/${clientId}/checks/unassigned`
    );
    return response.data;
  },

  /**
   * Get unassigned deposits for a client
   */
  getUnassignedDeposits: async (clientId: string): Promise<CustomerDeposit[]> => {
    const response = await apiClient.get<CustomerDeposit[]>(
      `/clients/${clientId}/deposits/unassigned`
    );
    return response.data;
  },

  /**
   * Run AI customer identification (returns job_id for background processing)
   */
  identifyCustomers: async (clientId: string): Promise<{ job_id: string }> => {
    const response = await apiClient.post<{ job_id: string }>(`/clients/${clientId}/identify-customers`);
    return response.data;
  },

  /**
   * Accept a potential customer
   */
  acceptPotentialCustomer: async (suggestionId: string): Promise<void> => {
    await apiClient.post(`/potential-customers/${suggestionId}/accept`);
  },

  /**
   * Reject a potential customer
   */
  rejectPotentialCustomer: async (suggestionId: string): Promise<void> => {
    await apiClient.post(`/potential-customers/${suggestionId}/reject`);
  },

  /**
   * Rename a potential customer
   */
  renamePotentialCustomer: async (suggestionId: string, newName: string): Promise<void> => {
    await apiClient.put(`/potential-customers/${suggestionId}/name`, { name: newName });
  },

  /**
   * Remove a check from a potential customer suggestion
   */
  removeCheckFromSuggestion: async (suggestionId: string, checkId: string): Promise<void> => {
    await apiClient.delete(`/potential-customers/${suggestionId}/checks/${checkId}`);
  },

  /**
   * Remove a deposit from a potential customer suggestion
   */
  removeDepositFromSuggestion: async (suggestionId: string, depositId: string): Promise<void> => {
    await apiClient.delete(`/potential-customers/${suggestionId}/deposits/${depositId}`);
  },

  /**
   * Get checks assigned to a specific customer
   */
  getCustomerChecks: async (clientId: string, customerId: string): Promise<Check[]> => {
    const response = await apiClient.get<Check[]>(
      `/clients/${clientId}/checks`,
      { params: { customer_id: customerId } }
    );
    return response.data;
  },

  /**
   * Get deposits assigned to a specific customer
   */
  getCustomerDeposits: async (clientId: string, customerId: string): Promise<CustomerDeposit[]> => {
    const response = await apiClient.get<CustomerDeposit[]>(
      `/clients/${clientId}/deposits`,
      { params: { customer_id: customerId } }
    );
    return response.data;
  },
};
